import { AgGridReact } from 'ag-grid-react';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';
import { usePrevious } from '../../../hooks/usePrevious';
import {
    AgGridEvent,
    ClientSideRowModelModule,
    GridApi,
    ColumnResizedEvent,
    FilterChangedEvent,
    GridReadyEvent,
    SelectionChangedEvent,
} from 'ag-grid-community';
import { Client } from '../../../../core/domainModels/clients/Client';
import { LicenseModel } from '../../../../core/domainModels/licences/LicenseModel';
import { generateGuid } from '../../../../core/helpers/guid';

import { licencesTableDefaultColumnsDefs, licencesTableColumnsDefinitions } from '../models/columnDefinitions';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../../../state/ApplicationState';
import {
    applyInitialSortFromDisplayPreferences,
    createTachoColumnState,
} from '../../../../core/domainModels/displayPreferences/tachoColumnState';
import { modifyLicencesDisplayPreferences, modifyLicencesTableFilters } from '../../../../state/licences/reducer';
import AgGridContextMenuWrapper from '../../../components/ContextMenu/AgGridContextMenuWrapper';

interface LicencesTableProps {
    licences: Array<LicenseModel>;
    clients: Array<Client>;
    searchQuery: string;
    onLicenceDoubleClicked: () => void;
    onSelectionChanged: (licences: Array<LicenseModel>) => void;
}
export const LicencesTable = (props: LicencesTableProps) => {
    const { formatMessage } = useIntl();
    const dispatch = useDispatch();
    const gridApi = useRef<GridApi>();

    const displayPreferences = useSelector((state: ApplicationState) => state.licences.displayPreferences);
    const tableFiltersState = useSelector((state: ApplicationState) => state.licences.tableFilters);

    const columnDefs = useMemo(
        () => licencesTableColumnsDefinitions(formatMessage, props.clients, displayPreferences),
        [formatMessage, props.clients, displayPreferences],
    );

    const rowsToDisplay = useMemo(() => {
        let array: Array<any> = [];

        props.licences.forEach(licence => {
            const client = props.clients.find(item => item.id === licence.clientId);
            const validTo = licence.licenseVersions.reduce((a, b) => (a.validTo > b.validTo ? a : b)).validTo;

            const lastValidToVersion = licence.licenseVersions.find(item => validTo === item.validTo);
            const validFrom = lastValidToVersion?.validFrom;
            array.push({
                ...licence,
                ...lastValidToVersion,
                nip: client?.nip,
                name: client?.name,
                id: generateGuid(),
                validFrom,
                validTo,
            });
        });
        return array;
    }, [props.licences]);
    const previousLicences = usePrevious(rowsToDisplay);

    useEffect(() => {
        const added = rowsToDisplay.filter(
            licence => !previousLicences?.find(prevLicences => prevLicences.frontId === licence.frontId),
        );
        const removed = previousLicences?.filter(
            prevLicence => !rowsToDisplay?.find(licence => prevLicence.frontId === licence.frontId),
        );
        const updated = rowsToDisplay.filter(licence => !added.some(addedLicence => addedLicence.frontId === licence.frontId));
        gridApi.current?.applyTransaction({
            add: added,
            remove: removed,
            update: updated,
        });
    }, [rowsToDisplay]);

    const rowsSelectionChanged = useCallback((event: SelectionChangedEvent) => {
        const selectedNodes = event.api.getSelectedNodes()?.map(node => node.data);
        if (!selectedNodes) {
            console.log('No selected rows.');
            return;
        }
        props.onSelectionChanged(selectedNodes);
    }, []);

    const onGridReady = useCallback(
        (params: GridReadyEvent) => {
            gridApi.current = params.api;
            applyInitialSortFromDisplayPreferences(params, displayPreferences, [{ colId: 'validFrom', sort: 'asc' }]);
            params.api.setFilterModel(tableFiltersState);
            params.api.setGridOption('rowData', rowsToDisplay);
        },
        [rowsToDisplay, displayPreferences, tableFiltersState],
    );

    useEffect(() => {
        gridApi.current?.refreshCells();
    }, [props.clients]);

    useEffect(() => {
        gridApi.current?.setGridOption('rowData', rowsToDisplay);
    }, [props.searchQuery]);

    function onColumnsChanged(event: AgGridEvent): void {
        const tachoColumnState = createTachoColumnState(event.api.getColumnState());
        dispatch(modifyLicencesDisplayPreferences(tachoColumnState));
    }
    const onColumnResized = (event: ColumnResizedEvent) => {
        if (event.finished && event.source === 'uiColumnResized') {
            return onColumnsChanged(event);
        }
    };
    const onTableFiltersStateChanged = (event: FilterChangedEvent) => {
        const filters = event.api.getFilterModel();
        dispatch(modifyLicencesTableFilters(filters));
    };

    return (
        <>
            <AgGridContextMenuWrapper gridApi={gridApi.current} showStatusBar>
                <AgGridReact
                    suppressPropertyNamesCheck
                    getRowId={params => params.data?.id ?? ''}
                    suppressDragLeaveHidesColumns
                    suppressContextMenu={true}
                    multiSortKey="ctrl"
                    rowSelection="single"
                    defaultColDef={licencesTableDefaultColumnsDefs}
                    columnDefs={columnDefs}
                    modules={[ClientSideRowModelModule]}
                    onGridReady={onGridReady}
                    onSelectionChanged={rowsSelectionChanged}
                    onRowDoubleClicked={params => {
                        params.data.isAnex && props.onLicenceDoubleClicked();
                    }}
                    suppressScrollOnNewData={true}
                    onSortChanged={event => onColumnsChanged(event)}
                    onColumnMoved={event => onColumnsChanged(event)}
                    onColumnResized={event => onColumnResized(event)}
                    onFilterChanged={event => onTableFiltersStateChanged(event)}
                />
            </AgGridContextMenuWrapper>
        </>
    );
};
